import { useState, useRef, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useLanguage } from '@/contexts/LanguageContext'
import { useNotifications } from '@/contexts/NotificationContext'
import { logError, responseErrorMessage, toUserMessage } from '@/utils/errorMessage'
import { turbineService, type Turbine, type Photo, type PhotoVersion } from '@/services/turbineService'
import { authService } from '@/services/authService'
import { log360 } from './shared'

// TODO(i18n): 360° ekranına özel metinler; strings.ts bu birimin sahibi değil.
const TURBINE_LOAD_ERROR = 'Türbin bilgileri yüklenemedi.'
const VERSIONS_LOAD_ERROR_TITLE = 'Versiyonlar yüklenemedi'
const VERSIONS_LOAD_ERROR = 'Fotoğraf versiyonları yüklenemedi. Mevcut görüntü korunuyor.'
const VERSION_DELETE_ERROR_TITLE = 'Versiyon silinemedi'
const VERSION_DELETE_ERROR = 'Versiyon silinirken bir hata oluştu. Lütfen tekrar deneyin.'

export const useTurbine360ViewModel = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const currentUser = authService.getCurrentUser()
  const { t } = useLanguage()
  // Bildirimler global toast katmanından (NotificationProvider) geliyor.
  // Eskiden bu ekranda İKİ ayrı sistem vardı: lokal ToastNotification (her zaman
  // YEŞİL -- hataları "başarılı" gibi gösteriyordu) ve tek slotluk NotificationModal.
  const { notifyError, notifyWarning, notifySuccess } = useNotifications()

  const [turbine, setTurbine] = useState<Turbine | null>(null)
  const [currentRoom, setCurrentRoom] = useState(1)
  // Blade seçimi burada tutuluyor ki sidebar (foto listesi) ve viewer (panorama
  // listesi) AYNI blade+oda filtresini paylaşsın; aksi halde iki liste ayrışıp
  // gezinme foto atlıyormuş gibi görünüyordu (1,3,5,7…).
  const [selectedBlade, setSelectedBlade] = useState('A')
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSidebarOpen] = useState(true)
  const [isCommentsOpen, setIsCommentsOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [viewerRefreshKey, setViewerRefreshKey] = useState<number>(0)
  const [, setUser] = useState<any>(null)
  const [photoVersions, setPhotoVersions] = useState<PhotoVersion[]>([])
  const [currentVersionIndex, setCurrentVersionIndex] = useState(0)
  // No longer blocks panorama loading; versions load in parallel
  const [loadingVersions, setLoadingVersions] = useState(false)
  const [isVersionMenuOpen, setIsVersionMenuOpen] = useState(false)
  const versionDropdownRef = useRef<HTMLDivElement>(null)
  const turbineId = turbine?.id ?? (id ? parseInt(id!, 10) : 0)

  const getVersionLabel = useCallback((version: PhotoVersion | undefined, index: number) => {
    if (!version) return 'Versiyon'
    if (version.is_original || index === 0) return 'Orijinal'
    const versionNumber = version.version_number ?? index
    return `Düzenleme ${versionNumber}`
  }, [])

  const formatVersionMeta = useCallback((version: PhotoVersion) => {
    const parts: string[] = []
    if (version.created_by) parts.push(version.created_by)
    if (version.created_at) {
      const createdDate = new Date(version.created_at)
      if (!isNaN(createdDate.getTime())) {
        // Format: "21 Aralık 2025 - 14:50"
        const formattedDate = createdDate.toLocaleDateString('tr-TR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        })
        const timeString = createdDate.toLocaleTimeString('tr-TR', {
          hour: '2-digit',
          minute: '2-digit'
        })
        parts.push(`${formattedDate} - ${timeString}`)
      }
    }
    return parts.join(' • ')
  }, [])

  // Versiyon URL'leri fotoğraf seçildiğinde taze üretiliyor (6 saat geçerli);
  // her versiyon geçişinde tüm listeyi yeniden çekmek gereksiz bir API turuydu.
  const handleVersionChange = useCallback((index: number) => {
    log360('handleVersionChange called', { index, isOriginal: index === 0, photoId: selectedPhoto?.id, turbineId })

    if (!selectedPhoto?.id || !turbineId) {
      setIsVersionMenuOpen(false)
      return
    }

    setCurrentVersionIndex(index)

    const version = photoVersions[index]
    if (version && selectedPhoto.url !== version.photo_url) {
      setSelectedPhoto({
        ...selectedPhoto,
        url: version.photo_url,
        key: version.photo_key,
        _originalKey: (selectedPhoto as any)._originalKey || selectedPhoto.key
      })
      setViewerRefreshKey(Date.now())
    }

    setIsVersionMenuOpen(false)
  }, [photoVersions, selectedPhoto, turbineId])

  const handleDeleteVersion = useCallback(async (index: number, version: PhotoVersion) => {
    if (!selectedPhoto || !turbineId || index === 0) return // Orijinal versiyonu silemesin

    if (!confirm(`"${getVersionLabel(version, index)}" versiyonunu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) {
      return
    }

    try {
      const response = await turbineService.deletePhotoVersion(turbineId, selectedPhoto.id!, version.id)

      if (response.success) {
        notifySuccess('Versiyon silindi', 'Seçilen versiyon kalıcı olarak silindi.') // TODO(i18n)

        // Versiyonları yeniden yükle
        const versionResponse = await turbineService.getPhotoVersions(turbineId, selectedPhoto.id!)
        if (versionResponse.success && versionResponse.data) {
          const freshVersions = [versionResponse.data.original, ...versionResponse.data.versions]
          setPhotoVersions(freshVersions)

          // Eğer silinen versiyon aktifse, bir önceki versiyona geç
          if (index === currentVersionIndex && freshVersions.length > 0) {
            const newIndex = Math.min(index - 1, freshVersions.length - 1)
            setCurrentVersionIndex(newIndex)

            const newVersion = freshVersions[newIndex]
            if (newVersion) {
              setSelectedPhoto({
                ...selectedPhoto,
                url: newVersion.photo_url,
                key: newVersion.photo_key,
                _originalKey: (selectedPhoto as any)._originalKey || selectedPhoto.key
              })
              setViewerRefreshKey(Date.now())
            }
          }
        } else {
          // Silme başarılı ama liste tazelenemedi: menü eski kalır. Sessiz geçilirse
          // kullanıcı silinen versiyonu listede görmeye devam eder.
          notifyWarning('Versiyon listesi yenilenemedi', responseErrorMessage(versionResponse, t, 'Silme tamamlandı ancak versiyon listesi güncellenemedi. Sayfayı yenileyin.')) // TODO(i18n)
        }
      } else {
        // success:false bir hatadır; backend mesajı varsa aynen gösterilir.
        notifyError(VERSION_DELETE_ERROR_TITLE, responseErrorMessage(response, t, VERSION_DELETE_ERROR))
      }
    } catch (error) {
      logError('Turbine360.deleteVersion', error)
      notifyError(VERSION_DELETE_ERROR_TITLE, toUserMessage(error, t, VERSION_DELETE_ERROR))
    }

    setIsVersionMenuOpen(false)
  }, [selectedPhoto, turbineId, currentVersionIndex, getVersionLabel, notifyError, notifyWarning, notifySuccess, t])

  // Version refresh trigger - increment this to force reload versions
  const [versionRefreshTrigger, setVersionRefreshTrigger] = useState(0)

  // Force refresh versions from API - called after save
  const forceRefreshVersions = useCallback(async () => {
    if (!selectedPhoto?.id || !turbineId) return
    log360('Force refreshing versions after save')
    setVersionRefreshTrigger(prev => prev + 1)
  }, [selectedPhoto?.id, turbineId])

  // Load photo versions when selection changes (top-level, not inside viewer)
  useEffect(() => {
    log360('Version load effect', { hasSelected: !!selectedPhoto, photoId: selectedPhoto?.id, turbineId, trigger: versionRefreshTrigger })
    if (!selectedPhoto?.id || !turbineId) {
      setPhotoVersions([])
      setCurrentVersionIndex(0)
      setLoadingVersions(false) // Allow panorama to load even without versions
      return
    }

    let cancelled = false
    const loadVersions = async () => {
      setLoadingVersions(true)
      try {
        const response = await turbineService.getPhotoVersions(turbineId, selectedPhoto.id as number)
        log360('Version response', response)
        if (!cancelled && response.success && response.data) {
          const allVersions = [response.data.original, ...response.data.versions]
          setPhotoVersions(allVersions)

          // Start with the latest version (last edited)
          // loadingVersions control in panorama effect prevents double loading
          const latestIndex = allVersions.length - 1
          setCurrentVersionIndex(latestIndex >= 0 ? latestIndex : 0)

          // Use the latest version's fresh URL
          const latestVersion = allVersions[latestIndex]
          if (latestVersion && selectedPhoto) {
            log360('Applying latest version to selection', { latestIndex, latestVersion })
            setSelectedPhoto(prev => {
              const base = prev || selectedPhoto
              return {
                ...base,
                url: latestVersion.photo_url,
                key: latestVersion.photo_key,
                _originalKey: (base as any)._originalKey || base.key
              }
            })
          }
        } else if (!cancelled) {
          if (response.success) {
            log360('No versions found, resetting state')
            setPhotoVersions([])
            setCurrentVersionIndex(0)
          } else {
            // "Hata != boş veri": istek patladıysa mevcut versiyon listesi KORUNUR
            // ve kullanıcı bilgilendirilir. Eskiden liste sessizce boşaltılıyordu,
            // versiyon menüsü de kaybolduğu için hata hiç görünmüyordu.
            console.error('❌ Photo versions request failed:', response.message)
            notifyError(VERSIONS_LOAD_ERROR_TITLE, responseErrorMessage(response, t, VERSIONS_LOAD_ERROR))
          }
          // 404 hatası alındıysa (fotoğraf silinmiş olabilir), selectedPhoto'yu temizleme
          // Kullanıcı yeni fotoğraf seçene kadar mevcut görüntüyü koru
        }
      } catch (error) {
        if (!cancelled) {
          logError('Turbine360.loadPhotoVersions', error)
          // Liste [] YAPILMIYOR: hata "versiyon yok" gibi görünmemeli.
          notifyError(VERSIONS_LOAD_ERROR_TITLE, toUserMessage(error, t, VERSIONS_LOAD_ERROR))
          // Hata durumunda da selectedPhoto'yu değiştirme - sonsuz döngüyü önle
        }
      } finally {
        if (!cancelled) setLoadingVersions(false)
      }
    }

    loadVersions()
    return () => { cancelled = true }
  }, [selectedPhoto?.id, turbineId, versionRefreshTrigger])

  // Yeni fotoğraf seçildiğinde eski versiyonları temizle
  useEffect(() => {
    // selectedPhoto değiştiğinde (key farklıysa) versiyonları sıfırla
    if (selectedPhoto && photoVersions.length > 0) {
      const currentOriginalKey = photoVersions[0]?.photo_key
      const selectedKey = (selectedPhoto as any)._originalKey || selectedPhoto.key
      if (currentOriginalKey && selectedKey && currentOriginalKey !== selectedKey) {
        log360('Photo changed, clearing old versions', { currentOriginalKey, selectedKey })
        setPhotoVersions([])
        setCurrentVersionIndex(0)
      }
    }
  }, [selectedPhoto?.key])

  useEffect(() => {
    setIsVersionMenuOpen(false)
  }, [selectedPhoto])

  useEffect(() => {
    const loadTurbineData = async () => {
      log360('Init Turbine360ViewScreen', { id })
      if (!id) {
        setError('Geçersiz türbin ID')
        return
      }
      
      try {
        setLoading(true)
        setError(null)
        const response = await turbineService.getTurbine(parseInt(id))
        log360('Turbine fetch response', response)

        if (response.success && response.turbine) {
          setTurbine(response.turbine)
        } else {
          // success:false bir hatadır; backend mesajı varsa aynen gösterilir.
          setError(responseErrorMessage(response, t, TURBINE_LOAD_ERROR))
        }
      } catch (error) {
        logError('Turbine360.loadTurbine', error)
        setError(toUserMessage(error, t, TURBINE_LOAD_ERROR))
      } finally {
        setLoading(false)
      }
    }
    
    const loadUserData = () => {
      try {
        const userData = localStorage.getItem('deepwind-user')
        if (userData) {
          setUser(JSON.parse(userData))
          log360('Loaded user data')
        }
      } catch (error) {
        // Bilerek sessiz: bu yalnızca localStorage'daki kopyayı okuyan bir
        // rahatlık adımı. Ekranın kullandığı kullanıcı bilgisi authService'ten
        // (currentUser) geliyor; bozuk JSON kullanıcı için görünür bir kayıp değil.
        console.error('Error loading user data:', error)
      }
    }
    
    loadTurbineData()
    loadUserData()
  }, [id])

  useEffect(() => {
    if (!isVersionMenuOpen) return
    const handleClickOutside = (event: MouseEvent) => {
      if (versionDropdownRef.current && !versionDropdownRef.current.contains(event.target as Node)) {
        setIsVersionMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isVersionMenuOpen])

  // Header'daki çıkış butonunun handler'ı (eski inline onClick gövdesi birebir)
  const handleLogout = async () => {
    try {
      await authService.logout()
      navigate('/')
    } catch (error) {
      // Yerel oturum her hâlükârda kapanıyor ve kullanıcı ana sayfaya gidiyor;
      // ama sunucu tarafı kapanmamış olabilir (token iptal edilmemiş olabilir).
      // Sessiz kalmak bunu gizlerdi.
      logError('Turbine360.logout', error)
      notifyWarning('Çıkış tamamlanamadı', 'Oturumunuz bu cihazda kapatıldı ancak sunucuda kapatılamadı. Güvenlik için tarayıcıyı kapatın.') // TODO(i18n)
      navigate('/')
    }
  }

  return {
    // state
    turbine,
    currentRoom,
    setCurrentRoom,
    selectedBlade,
    setSelectedBlade,
    selectedPhoto,
    setSelectedPhoto,
    error,
    isSidebarOpen,
    isCommentsOpen,
    setIsCommentsOpen,
    loading,
    viewerRefreshKey,
    setViewerRefreshKey,
    photoVersions,
    currentVersionIndex,
    loadingVersions,
    isVersionMenuOpen,
    setIsVersionMenuOpen,
    versionDropdownRef,
    turbineId,
    currentUser,
    // actions
    getVersionLabel,
    formatVersionMeta,
    handleVersionChange,
    handleDeleteVersion,
    forceRefreshVersions,
    handleLogout,
  }
}
